import { useState, useEffect, useRef } from 'react'
import { getApiInstance } from '../utils/api'

const API_PATH = '/api/GraficosUsuario'

function stripCodGrafico(w) {
  const { _codGrafico, ...rest } = w
  return rest
}

function parseSavedWidget(item) {
  const codGrafico = item.CodGrafico ?? item.codGrafico
  if (!codGrafico) return null
  const rawConfig = item.GraficoConfig ?? item.graficoConfig
  const parsed = typeof rawConfig === 'string'
    ? JSON.parse(rawConfig)
    : rawConfig
  return {
    id: `db-${codGrafico}`,
    metric: { agg: 'sum', field: 'peso' },
    secondMetric: null,
    filters: { logic: 'AND', conditions: [] },
    chartType: 'bar',
    dimension: '',
    dateGranularity: 'month',
    breakdown: '',
    sort: 'valueDesc',
    limit: 8,
    groupOthers: false,
    span: 1,
    height: 'md',
    showLegend: true,
    compact: false,
    ...parsed,
    _codGrafico: codGrafico,
  }
}

function configHash(widgets) {
  return JSON.stringify(widgets.map(w => stripCodGrafico(w)))
}

export function useGraficosUsuario() {
  const [widgets, setWidgets] = useState([])
  const [loading, setLoading] = useState(true)
  const [saveStatus, setSaveStatus] = useState('saved')

  const mountedRef = useRef(true)
  const syncingRef = useRef(false)
  const savedHashRef = useRef('')
  const loadedIdsRef = useRef(new Set())
  const widgetHashesRef = useRef(new Map())

  const saveToServer = async (currentWidgets) => {
    if (!mountedRef.current || syncingRef.current) return
    syncingRef.current = true
    setSaveStatus('saving')

    try {
      const api = getApiInstance()

      const currentIds = new Set(
        currentWidgets.map(w => w._codGrafico).filter(Boolean)
      )

      const toDelete = [...loadedIdsRef.current].filter(id => !currentIds.has(id))
      const toCreate = []
      const toUpdate = []

      currentWidgets.forEach(w => {
        if (!w._codGrafico) {
          toCreate.push(w)
        } else if (loadedIdsRef.current.has(w._codGrafico)) {
          const prevHash = widgetHashesRef.current.get(w._codGrafico) || ''
          const curHash = JSON.stringify(stripCodGrafico(w))
          if (curHash !== prevHash) {
            toUpdate.push(w)
          }
        }
      })

      console.log('[Graficos] DELETE:', toDelete.length, '| CREATE:', toCreate.length, '| UPDATE:', toUpdate.length)

      let hadError = false
      const fail = (e, op) => {
        hadError = true
        console.error(`[Graficos] ${op} error:`, e)
      }

      await Promise.allSettled(
        toDelete.map(id =>
          api.delete(`${API_PATH}/DeleteGraficoUsuario/${id}`).catch(e => fail(e, 'DELETE'))
        )
      )

      await Promise.allSettled(
        toUpdate.map(w =>
          api.put(`${API_PATH}/EditGraficoUsuario`, {
            CodGrafico: w._codGrafico,
            GraficoConfig: JSON.stringify(stripCodGrafico(w)),
          }).catch(e => fail(e, 'UPDATE'))
        )
      )

      const createdResults = await Promise.allSettled(
        toCreate.map(w =>
          api.post(`${API_PATH}/SetGraficoUsuario`, {
            GraficoConfig: JSON.stringify(stripCodGrafico(w)),
          }).then(r => r.data)
        )
      )

      createdResults.forEach(r => {
        if (r.status === 'rejected') {
          hadError = true
          console.error('[Graficos] CREATE error:', r.reason)
        }
      })

      const createdMap = new Map()
      toCreate.forEach((w, i) => {
        if (createdResults[i].status === 'fulfilled' && createdResults[i].value != null) {
          createdMap.set(w, createdResults[i].value)
        }
      })

      const rebuilt = currentWidgets.map(w => {
        const newId = createdMap.get(w)
        if (newId != null) return { ...w, _codGrafico: newId }
        return w
      })

      loadedIdsRef.current = new Set(rebuilt.map(w => w._codGrafico).filter(Boolean))
      savedHashRef.current = configHash(rebuilt)

      widgetHashesRef.current = new Map()
      rebuilt.forEach(w => {
        if (w._codGrafico) {
          widgetHashesRef.current.set(w._codGrafico, JSON.stringify(stripCodGrafico(w)))
        }
      })

      if (mountedRef.current) {
        setWidgets(rebuilt)
        setSaveStatus(hadError ? 'error' : 'saved')
      }
      console.log('[Graficos] saveToServer, hadError =', hadError)
    } catch (err) {
      console.error('[Graficos] Error guardando:', err)
      if (mountedRef.current) setSaveStatus('error')
    } finally {
      syncingRef.current = false
    }
  }

  useEffect(() => {
    mountedRef.current = true
    const api = getApiInstance()

    api.get(`${API_PATH}/GetGraficosByUsuario`)
      .then(res => {
        if (!mountedRef.current) return
        const data = res.data

        if (data && data.length > 0) {
          const loaded = data.map(parseSavedWidget).filter(Boolean)
          loadedIdsRef.current = new Set(loaded.map(w => w._codGrafico).filter(Boolean))
          savedHashRef.current = configHash(loaded)
          widgetHashesRef.current = new Map()
          loaded.forEach(w => {
            if (w._codGrafico) {
              widgetHashesRef.current.set(w._codGrafico, JSON.stringify(stripCodGrafico(w)))
            }
          })
          setWidgets(loaded)
        }

        setLoading(false)
      })
      .catch(() => {
        if (!mountedRef.current) return
        setLoading(false)
      })

    return () => { mountedRef.current = false }
  }, [])

  useEffect(() => {
    if (loading) return
    const hash = configHash(widgets)
    if (hash === savedHashRef.current) return
    const timer = setTimeout(() => saveToServer(widgets), 800)
    return () => clearTimeout(timer)
  }, [widgets, loading])

  return { widgets, setWidgets, loading, saveStatus }
}
